"use client";

import { useEffect } from "react";
import {
  LuLeaf,
  LuMoon,
  LuRefreshCcw,
  LuSun,
  LuWifi,
  LuWifiOff,
  LuZap,
} from "react-icons/lu";
import { useDebouncedCallback } from "use-debounce";

import { Button } from "@/components/ui/button";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import { useLayout } from "@/contexts/layout-context";
import { useOwnTheme } from "@/contexts/theme-context";
import {
  THEME_COLOR_PRESET,
  THEME_COLORS,
  THEME_OPTIONS,
} from "@/utils/constants";
import { cn } from "@/utils/lib";

const DUCKY_CIRCLE_GESTURE_TIMEOUT_MS = 6000;
const DUCKY_CIRCLE_INNER_CANCEL_RADIUS = 8;
const DUCKY_CIRCLE_MIN_RADIUS = 24;
const DUCKY_CIRCLE_MAX_RADIUS = 110;
const DUCKY_CIRCLE_OUTER_CANCEL_RADIUS = 150;
const DUCKY_CIRCLE_START_MOVEMENT = 12;
const DUCKY_CIRCLE_SWEEP = Math.PI * 1.75;
const DUCKY_THEME_TRIGGER_SELECTOR = "[data-ducky-theme-trigger]";

type DuckyCircleGesture = {
  active: boolean;
  centerX: number;
  centerY: number;
  lastAngle: number | null;
  pointerId: number;
  startX: number;
  startY: number;
  timeout: ReturnType<typeof setTimeout>;
  totalAngle: number;
};

let duckyCircleGesture: DuckyCircleGesture | null = null;
let suppressDuckyTriggerClickUntil = 0;

const normalizeAngleDelta = (delta: number) => {
  if (delta > Math.PI) {
    return delta - Math.PI * 2;
  }

  if (delta < -Math.PI) {
    return delta + Math.PI * 2;
  }

  return delta;
};

const stopDuckyTriggerEvent = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
};

const ThemeColorSelector = () => {
  const { theme, customColor, setCustomColor } = useOwnTheme();
  const debouncedUpdateCustomColor = useDebouncedCallback((v) => {
    setCustomColor(v);
    document.querySelector("html")?.style.removeProperty("--primary");
  }, 300);

  const className = `
    relative size-5 cursor-pointer rounded-full
    hover:shadow-[0_0_0_1px_var(--background),0_0_0_2px_var(--muted-foreground)]
  `;
  const selectedClassName =
    "shadow-[0_0_0_1px_var(--background),0_0_0_2px_var(--muted-foreground)]";

  return (
    <div
      className="
        mt-3 grid grid-cols-5 items-center gap-2 border-t p-2 pt-4 text-center
      "
    >
      {THEME_COLOR_PRESET.map((color) => (
        <div
          key={color}
          className={cn(className, {
            [selectedClassName]: customColor === color,
          })}
          style={{ backgroundColor: color }}
          onClick={() => {
            setCustomColor(color);
          }}
        />
      ))}
      <div
        className={cn(
          className,
          `
            bg-[conic-gradient(from_0deg,red,orange,yellow,green,blue,indigo,violet,red)]
          `,
          customColor &&
            !THEME_COLOR_PRESET.includes(customColor) &&
            selectedClassName,
        )}
      >
        <input
          className="absolute inset-0 size-full cursor-pointer opacity-0"
          type="color"
          value={customColor || (theme && THEME_COLORS[theme]) || ""}
          onChange={(e) => {
            document
              .querySelector("html")
              ?.style.setProperty("--primary", e.target.value);
            debouncedUpdateCustomColor(e.target.value);
          }}
        />
      </div>
      <div>
        <LuRefreshCcw
          className="
            ml-0.5 size-4 cursor-pointer text-muted
            hover:text-muted-foreground
          "
          onClick={() => {
            setCustomColor(null);
          }}
        />
      </div>
    </div>
  );
};

type ThemeToggleProps = {
  align?: "start" | "end";
};

export function ThemeToggle({ align = "end" }: ThemeToggleProps) {
  const { theme, setTheme } = useOwnTheme();
  const { setEnableDucky } = useLayout();

  useEffect(() => {
    const resetDuckyCircleGesture = () => {
      if (duckyCircleGesture?.timeout) {
        clearTimeout(duckyCircleGesture.timeout);
      }

      duckyCircleGesture = null;
    };

    const onPointerDown = (event: PointerEvent) => {
      const trigger = (event.target as Element | null)?.closest<HTMLElement>(
        DUCKY_THEME_TRIGGER_SELECTOR,
      );

      if (!trigger) {
        return;
      }

      if (event.button !== 0 || duckyCircleGesture) {
        return;
      }

      const rect = trigger.getBoundingClientRect();

      duckyCircleGesture = {
        active: false,
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2,
        lastAngle: null,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        timeout: setTimeout(
          resetDuckyCircleGesture,
          DUCKY_CIRCLE_GESTURE_TIMEOUT_MS,
        ),
        totalAngle: 0,
      };
    };

    const onPointerMove = (event: PointerEvent) => {
      const gesture = duckyCircleGesture;

      if (!gesture || event.pointerId !== gesture.pointerId) {
        return;
      }

      const startDistance = Math.hypot(
        event.clientX - gesture.startX,
        event.clientY - gesture.startY,
      );

      if (!gesture.active && startDistance < DUCKY_CIRCLE_START_MOVEMENT) {
        return;
      }

      gesture.active = true;
      stopDuckyTriggerEvent(event);

      const radius = Math.hypot(
        event.clientX - gesture.centerX,
        event.clientY - gesture.centerY,
      );

      if (
        radius < DUCKY_CIRCLE_INNER_CANCEL_RADIUS ||
        radius > DUCKY_CIRCLE_OUTER_CANCEL_RADIUS
      ) {
        suppressDuckyTriggerClickUntil = Date.now() + 500;
        resetDuckyCircleGesture();
        return;
      }

      if (
        radius < DUCKY_CIRCLE_MIN_RADIUS ||
        radius > DUCKY_CIRCLE_MAX_RADIUS
      ) {
        return;
      }

      const angle = Math.atan2(
        event.clientY - gesture.centerY,
        event.clientX - gesture.centerX,
      );

      if (gesture.lastAngle === null) {
        gesture.lastAngle = angle;
        return;
      }

      gesture.totalAngle += normalizeAngleDelta(angle - gesture.lastAngle);
      gesture.lastAngle = angle;

      if (Math.abs(gesture.totalAngle) >= DUCKY_CIRCLE_SWEEP) {
        suppressDuckyTriggerClickUntil = Date.now() + 500;
        resetDuckyCircleGesture();
        setEnableDucky((prev) => !prev);
      }
    };

    const onPointerEnd = (event: PointerEvent) => {
      const gesture = duckyCircleGesture;

      if (!gesture || event.pointerId !== gesture.pointerId) {
        return;
      }

      if (gesture.active) {
        suppressDuckyTriggerClickUntil = Date.now() + 500;
        stopDuckyTriggerEvent(event);
      }

      resetDuckyCircleGesture();
    };

    const onClick = (event: MouseEvent) => {
      if (Date.now() > suppressDuckyTriggerClickUntil) {
        return;
      }

      const trigger = (event.target as Element | null)?.closest<HTMLElement>(
        DUCKY_THEME_TRIGGER_SELECTOR,
      );

      if (trigger) {
        stopDuckyTriggerEvent(event);
      }
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointermove", onPointerMove, true);
    document.addEventListener("pointerup", onPointerEnd, true);
    document.addEventListener("pointercancel", onPointerEnd, true);
    document.addEventListener("click", onClick, true);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointermove", onPointerMove, true);
      document.removeEventListener("pointerup", onPointerEnd, true);
      document.removeEventListener("pointercancel", onPointerEnd, true);
      document.removeEventListener("click", onClick, true);
    };
  }, [setEnableDucky]);

  const getIcon = () => {
    switch (theme) {
      case THEME_OPTIONS.lightMode:
        return <LuWifiOff className="size-[1.2rem]" />;
      case THEME_OPTIONS.darkMode:
        return <LuWifi className="size-[1.2rem]" />;
      case THEME_OPTIONS.modernLightMode:
        return <LuSun className="size-[1.2rem]" />;
      case THEME_OPTIONS.modernDarkMode:
        return <LuMoon className="size-[1.2rem]" />;
      case THEME_OPTIONS.paperLightMode:
        return <LuLeaf className="size-[1.2rem]" />;
      case THEME_OPTIONS.paperDarkMode:
        return <LuZap className="size-[1.2rem]" />;
      default:
        return <LuSun className="size-[1.2rem]" />;
    }
  };

  return (
    <DropdownDrawer historyName="theme-toggle" title="Theme">
      <DropdownDrawerTrigger
        render={
          <Button
            variant="outline"
            size="icon"
            className="shrink-0 touch-none rounded-full border-border"
            data-ducky-theme-trigger
          >
            {getIcon()}
            <span className="sr-only">Toggle theme</span>
          </Button>
        }
      />

      <DropdownDrawerContent align={align}>
        <DropdownDrawerItem
          className="cursor-pointer"
          onClick={() => setTheme(THEME_OPTIONS.lightMode)}
        >
          Classic Light
        </DropdownDrawerItem>

        <DropdownDrawerItem
          className="cursor-pointer"
          onClick={() => setTheme(THEME_OPTIONS.darkMode)}
        >
          Classic Dark
        </DropdownDrawerItem>

        <DropdownDrawerItem
          className="cursor-pointer"
          onClick={() => setTheme(THEME_OPTIONS.modernLightMode)}
        >
          Modern Light
        </DropdownDrawerItem>

        <DropdownDrawerItem
          className="cursor-pointer"
          onClick={() => setTheme(THEME_OPTIONS.modernDarkMode)}
        >
          Modern Dark
        </DropdownDrawerItem>

        <DropdownDrawerItem
          className="cursor-pointer"
          onClick={() => setTheme(THEME_OPTIONS.paperLightMode)}
        >
          Paper Light
        </DropdownDrawerItem>

        <DropdownDrawerItem
          className="cursor-pointer"
          onClick={() => setTheme(THEME_OPTIONS.paperDarkMode)}
        >
          Paper Dark
        </DropdownDrawerItem>

        <ThemeColorSelector />
      </DropdownDrawerContent>
    </DropdownDrawer>
  );
}
